import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { POLL_WINDOW_S } from '@/shared/hooks/useCommandConfirmation';
import UnreachableNotice from '@/shared/components/UnreachableNotice';
import { ageJoignabilite, etatJoignabilite } from '@/shared/utils/liveness';

/**
 * Compte rendu d'UNE commande de pilotage.
 *
 * Sépare deux choses que l'ancien toast confondait :
 *   1. le backend a-t-il accepté et publié l'ordre ?   (réponse HTTP, immédiate)
 *   2. l'actif l'a-t-il suivi ?                        (relecture de la télémétrie)
 *
 * Un ordre publié sur un device hors ligne coche la première et pas la seconde. Le dire
 * est le seul moyen pour l'admin de savoir s'il doit rappeler le client.
 *
 * @param {object} result   { success, detail, simulated, error } + { asset, actionLabel, value, valueUnit, at }
 * @param {object} confirm  { phase, telemetry } issu de useCommandConfirmation
 */
export default function PilotageResultDialog({ result, confirm, suiviOrdre, onClose }) {
  if (!result) return null;
  const { asset, actionLabel, value, valueUnit, at } = result;
  const failed = result.success === false;

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="pilotage-result-dialog">
        <DialogHeader><DialogTitle>Résultat du pilotage</DialogTitle></DialogHeader>

        <div className="space-y-3 text-[13px]">
          <div className="p-3 rounded-md bg-neutral-50 border border-neutral-100">
            <p className="text-[11px] text-neutral-400">Actif ciblé</p>
            <p className="font-medium text-neutral-900">
              {asset.client_nom} — {asset.type} {asset.marque}
            </p>
          </div>

          <p className="text-neutral-700">
            Action : <span className="font-semibold text-neutral-900">{actionLabel}</span>
            {value != null && (
              <span className="ml-1 text-cyan-700 font-semibold">
                → {value} {valueUnit || ''}
              </span>
            )}
          </p>

          {failed ? (
            <Line
              tone="error"
              icon={XCircle}
              title="Ordre refusé — rien n'a été publié"
              detail={result.error || "L'actif n'a pas exécuté l'ordre"}
              testId="result-rejected"
            />
          ) : result.confirme === false ? (
            /* TROISIÈME ÉTAT, ajouté avec le pilotage des centrales PV (10/09/2026).
               Hoymiles rend un accusé de réception, pas un succès : l'exécution réelle se
               relit ensuite. Entre « refusé » et « exécuté » il y a donc « parti, on ne
               sait pas encore » — et c'est une information, pas une demi-victoire.
               Le peindre en vert reproduirait exactement le 28/07 cité plus bas.
               Depuis le 30/09 (retour de David), la popup suit l'ordre et passe d'elle-même
               à l'état final, que seul le résultat relu chez Hoymiles peut décider. */
            <SuiviOrdreS resultat={suiviOrdre?.resultat} libelle={suiviOrdre?.libelle} />
          ) : (
            /* Une coche verte sur un ordre envoyé à une machine qui ne répondait pas,
               c'est précisément le message qui a rassuré à tort le 28/07. Quand le
               backend nous dit que le device n'était pas joignable, le ton change. */
            <Line
              tone={result.device_joignable === 'non' ? 'attente' : 'ok'}
              icon={result.device_joignable === 'non' ? AlertTriangle : CheckCircle2}
              title={result.simulated
                ? 'Geste enregistré (action simulée)'
                : result.device_joignable === 'non'
                  ? "Ordre envoyé, mais la machine ne répondait pas"
                  : "Ordre accepté et envoyé à l'équipement"}
              detail={result.detail}
              testId="result-accepted"
            />
          )}

          {result.simulated && (
            <div
              className="flex items-start gap-2 p-3 rounded-md bg-neutral-100 border border-neutral-200 text-[12px] text-neutral-700"
              data-testid="result-simulated"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-neutral-500" />
              <span>
                Aucune intégration n'est branchée pour cette action : elle est inscrite au
                journal mais <strong>n'a eu aucun effet sur l'équipement</strong>.
              </span>
            </div>
          )}

          {!failed && !result.simulated && result.confirme !== false && (
            <ConfirmationBlock
              confirm={confirm} asset={asset} batteryAction={result.batteryAction} />
          )}

          <p className="text-[11px] text-neutral-400 pt-2 border-t border-neutral-100">
            Tracé au journal avec votre nom{at ? ` · ${at}` : ''}.
          </p>
        </div>

        <DialogFooter>
          <Button onClick={onClose} className="w-full" data-testid="pilotage-result-close">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** L'ordre S-Miles, tant qu'il n'est pas tranché puis une fois tranché. */
function SuiviOrdreS({ resultat, libelle }) {
  if (resultat === 'reussi') {
    return (
      <Line tone="ok" icon={CheckCircle2} title="Exécution confirmée par l'installation"
            detail={libelle || "Hoymiles confirme que l'ordre a été appliqué."}
            testId="result-ordre-confirme" />
    );
  }
  if (resultat === 'echoue') {
    return (
      <Line tone="error" icon={XCircle} title="Ordre refusé par l'installation"
            detail={libelle || "L'installation n'a pas appliqué l'ordre."}
            testId="result-ordre-refuse" />
    );
  }
  if (resultat === 'sans_reponse') {
    return (
      <Line tone="attente" icon={AlertTriangle} title="Aucune réponse de l'installation"
            detail="Son exécution restera inconnue : vérifiez la production relevée."
            testId="result-ordre-sans-reponse" />
    );
  }
  return (
    <Line
      tone="attente"
      icon={Loader2}
      title="Ordre envoyé — exécution pas encore confirmée"
      detail={"L'installation doit maintenant l'appliquer. Flexcore relit le résultat "
              + 'automatiquement : la confirmation s\'affiche ici, en général en quelques '
              + 'secondes, au plus tard dans les cinq minutes.'}
      testId="result-envoye-non-confirme"
    />
  );
}

/** Second temps : ce que la télémétrie dit de l'état réel de l'actif. */
function ConfirmationBlock({ confirm, asset, batteryAction }) {
  const phase = confirm?.phase;
  const t = confirm?.telemetry;
  // Sans ce rappel, « n'a pas suivi (état : charge) » se lit comme une contradiction :
  // l'état peut être le bon alors que le signe de la puissance ne l'est pas.
  const expectedLabel = batteryAction === 'charge'
    ? <>l'état <strong>charge</strong> avec une puissance négative</>
    : <>l'état <strong>décharge</strong> avec une puissance positive</>;

  if (phase === 'pending') {
    return (
      <div className="flex items-center gap-2 text-[12px] text-neutral-500" data-testid="result-pending">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Vérification de l'état réel de l'actif…</span>
      </div>
    );
  }

  if (phase === 'confirmed') {
    return (
      <Line
        tone="ok"
        icon={CheckCircle2}
        title={`État confirmé : ${t?.etat}`}
        detail={telemetryLine(t)}
        testId="result-confirmed"
      />
    );
  }

  // Avant « pas encore constaté » : une machine injoignable n'est pas une machine lente.
  // La boucle s'arrête dès qu'elle le sait, on n'a donc pas attendu la minute complète.
  if (phase === 'unreachable') {
    return <UnreachableNotice etat={etatJoignabilite(t)} ageS={ageJoignabilite(t)} />;
  }

  if (phase === 'unconfirmed') {
    return (
      <div
        className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-100 text-[12px] text-amber-800"
        data-testid="result-unconfirmed"
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
        <span>
          <strong>Pas encore constaté</strong> après {POLL_WINDOW_S} secondes — ce n'est
          pas un échec.
          {t && (
            <>
              {' '}Dernier état relu : <strong>{t.etat || 'inconnu'}</strong>
              {t.puissance_kw != null && <> à <strong>{t.puissance_kw} kW</strong></>}
              {' '}— on attendait {expectedLabel}.
            </>
          )}{' '}
          Le matériel peut mettre plusieurs minutes à réagir, et une consigne de charge
          n'aboutit que s'il y a de quoi charger. <strong>L'ordre reste publié et
          tracé</strong> ; l'écran continue de se rafraîchir tout seul.
        </span>
      </div>
    );
  }

  if (phase === 'skipped') {
    return (
      <p className="text-[11px] text-neutral-400" data-testid="result-skipped">
        {asset.type === 'Batterie'
          ? "Cet ordre n'a pas d'état distinctif à relire : « veille » et 0 W sont aussi l'état d'une batterie qui n'a rien reçu."
          : `Pas de relecture d'état disponible pour un actif ${asset.type}.`}
        {t ? ` ${telemetryLine(t)}` : ''}
      </p>
    );
  }

  return null;
}

function telemetryLine(t) {
  if (!t) return null;
  const parts = [];
  if (t.puissance_kw != null) parts.push(`Réel ${t.puissance_kw} kW`);
  if (t.soc_pct != null) parts.push(`SOC ${t.soc_pct} %`);
  return parts.join(' · ') || null;
}

/** Ligne de statut : une icône teintée, un titre, un détail discret. */
function Line({ tone, icon: Icon, title, detail, testId }) {
  const tones = {
    ok: 'text-emerald-600',
    attente: 'text-amber-700',
    error: 'text-red-600',
  };
  return (
    <div className="flex items-start gap-2" data-testid={testId}>
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tones[tone]}`} />
      <div>
        <p className={`text-[12px] font-medium ${tones[tone]}`}>{title}</p>
        {detail && <p className="text-[11px] text-neutral-500">{detail}</p>}
      </div>
    </div>
  );
}
